import React, { useEffect, useState } from 'react';
import { Container, Grid, Stack, Switch, Typography, FormControl, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { dispatch, useSelector } from '../../../../redux/store';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { toast } from 'react-toastify';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { getMonthlySalesReport } from '../../../../redux/slices/reportsRedux/reportRedux';
import { fCurrency } from '../../../../utils/formatNumber';
import useSettings from '../../../../hooks/useSettings';
import { accessVerify } from '../../../../utils/common';
import moment from 'moment';
import { DatePicker } from '@mui/lab';

export default function MonthlySalesReport() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const { monthlySalesReport: monthlyReportData } = useSelector((state) => state.report);

  const [isShowAll, setIsShowAll] = useState(true);
  const [dataList, setDataList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(moment());

  useEffect(() => {
    if (!selectedLocation) return;
    if (!selectedMonth) return;

    const startDate = moment(selectedMonth).startOf('month').format('YYYY-MM-DD HH:mm:ss');
    const endDate = moment(selectedMonth).endOf('month').format('YYYY-MM-DD HH:mm:ss');

    dispatch(getMonthlySalesReport(selectedLocation?.id, startDate, endDate));
  }, [selectedLocation, selectedMonth]);

  useEffect(() => {
    if (monthlyReportData?.length > 0) {
      setDataList(monthlyReportData);
    } else {
      setDataList([]);
    }
  }, [monthlyReportData]);

  const columns = [
    { accessorKey: 'date', header: 'Date' },
    { accessorKey: 'coinMaster', header: 'Coin Master' },
    {
      accessorKey: 'dailyTarget',
      header: 'Sales Target',
      Cell: ({ cell, row }) => {
        return fCurrency(row.original.dailyTarget || 0);
      },
    },
    {
      accessorKey: 'dailySale',
      header: 'Total Deposited Sales',
      Cell: ({ cell, row }) => {
        return fCurrency(row.original.dailySale || 0);
      },
    },
    {
      accessorKey: 'difference',
      header: 'Difference',
      Cell: ({ cell, row }) => {
        return fCurrency(row.original.difference || 0);
      },
    },
    { accessorKey: 'walkingCustomerCount', header: 'Walking Customers' },
    { accessorKey: 'returningCustomers', header: 'Returning Customers' },
    { accessorKey: 'newCustomersCount', header: 'New Customers ' },
    // { accessorKey: 'newCustomers', header: 'New Customer names' },
    {
      accessorKey: 'walkingCustomerAvgSpent',
      header: 'Walking Customer Average Spent',
      Cell: ({ cell, row }) => {
        return fCurrency(row.original.walkingCustomerAvgSpent || 0);
      },
    },
    { accessorKey: 'pickMeCustomerCount', header: 'PickMe Customers' },
    { accessorKey: 'uberCustomerCount', header: 'Uber Customers' },
    { accessorKey: 'googleReviewCount', header: 'Google Reviews' },
  ];

  return (
    <Page title='Monthly Sales Report'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Monthly Sales Report (Under Development 👨‍💻)'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: '' }, { name: 'Monthly Sales Report' }]}
        />

        {/* Month Selector */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <DatePicker
                label='Select Month'
                value={selectedMonth}
                onChange={(newValue) => {
                  if (!newValue) return;
                  setSelectedMonth(moment(newValue));
                }}
                views={['year', 'month']}
                openTo='month'
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>
          </Grid>
        </Grid>

        {selectedLocation && accessVerify('MONTHLY_SALES_REPORT_VIEW') ? (
          <DataGridTable
            name='Monthly Sales Report'
            data={dataList}
            column={columns}
            isLoading={false}
            enableRowActions={false}
            enablePinning={false}
            enableRowSelection={false}
            isRowClickable={false}
            rowsPerPage={40}
          />
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
